import { Link } from "react-router-dom";
import { Mail, LifeBuoy, Compass, BookOpen } from "lucide-react";
import { useSeo, CONTACT_ROUTES, SAFEGUARDING_LEAD, OPERATOR } from "@/lib/site";

const Address = ({ r }) => (
  <span className="block">
    <a href={`mailto:${r.email}?subject=${encodeURIComponent(r.subject)}`} className="font-semibold text-teal underline underline-offset-4 break-all" data-testid={`contact-email-${r.id}`}>{r.email}</a>
    <span className="mt-1 block text-sm text-ink-muted">Please use the subject line “{r.subject}”.</span>
  </span>
);

const RouteCard = ({ r }) => (
  <section id={r.id} className="rounded-3xl bg-white/70 border border-ink/10 p-6 sm:p-8 scroll-mt-24" aria-labelledby={`${r.id}-h`} data-testid={`contact-route-${r.id}`}>
    <Mail className="h-6 w-6 text-teal" aria-hidden="true" />
    <h2 id={`${r.id}-h`} className="mt-4 text-2xl">{r.title}</h2>
    <p className="mt-3 text-ink-soft">{r.for}</p>
    <p className="mt-3 text-sm text-ink-soft"><strong>Please include:</strong> {r.include}</p>
    <p className="mt-5"><Address r={r} /></p>
  </section>
);

const SignpostLink = ({ to, Icon, label, testId }) => (
  <Link to={to} className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 min-h-[44px] font-semibold text-teal hover:bg-cream transition-colors" data-testid={testId}>
    <Icon className="h-4 w-4" aria-hidden="true" /> {label}
  </Link>
);

export default function Contact() {
  useSeo("Contact", "How to contact Addiction & Us about partnerships, privacy, unsafe content or website issues.");
  return (
    <div className="container-site pt-14 md:pt-20" data-testid="contact-page">
      <p className="eyebrow mb-4">Contact</p>
      <h1 className="text-4xl sm:text-5xl leading-[1.05] max-w-3xl">Contacting Addiction &amp; Us</h1>
      <div className="mt-8 rounded-3xl bg-teal-light p-6 sm:p-8 max-w-4xl" data-testid="contact-support-signpost">
        <h2 className="text-2xl">Looking for support for yourself or someone you love?</h2>
        <p className="mt-3 text-ink-soft">We can't offer individual advice, counselling or replies about personal situations, and these addresses aren't monitored around the clock. Our guided journeys and the trusted organisations in our directory are the quickest way to find help that fits your situation.</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <SignpostLink to="/urgent-help" Icon={LifeBuoy} label="Urgent help" testId="contact-urgent-link" />
          <SignpostLink to="/find-your-way" Icon={Compass} label="Find Your Way" testId="contact-journeys-link" />
          <SignpostLink to="/directory" Icon={BookOpen} label="Support directory" testId="contact-directory-link" />
        </div>
        <p className="mt-5 font-semibold" data-testid="contact-urgent-note">Messages are not monitored continuously. If you need urgent help, please don't email: call <a href="tel:999" className="underline">999</a> if anyone is in danger, <a href="tel:111" className="underline">NHS 111</a> for urgent medical advice, or Samaritans free on <a href="tel:116123" className="underline">116 123</a>, any time.</p>
      </div>
      <div className="mt-12 grid gap-6 md:grid-cols-2">{CONTACT_ROUTES.map((r) => <RouteCard key={r.id} r={r} />)}</div>
      <div className="mt-10 max-w-4xl space-y-3 text-ink-soft" data-testid="contact-small-print">
        <p>Reports about unsafe content are reviewed by {SAFEGUARDING_LEAD}. Messages are checked regularly but not continuously, so this is not an emergency service. When a report shows that information on the site may be wrong or unsafe, we'll hide or correct that content first and then review it.</p>
        <p>We read every message, but we can't promise a reply to every one, and we don't reply about personal circumstances. Please send only the information we need.</p>
        <p>{OPERATOR}.</p>
      </div>
    </div>
  );
}
